import React, { useEffect, useState } from "react";
import {
    Table,
    Button,
    Form,
    Input,
    Space,
    message,
    Switch,
    Skeleton,
    Typography,
    notification,
    Tooltip,
    Popconfirm,
    Segmented
} from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
    getLanguages,
    createLanguage,
    updateLanguage,
    updateLanguageStatus
} from "../services/languageservice";
import { LanguageDto } from "../types/languagetypes";
import LanguageDrawer from "../components/languages/languagedrawer";
//import { useAuth } from "../auth/azureaad/authcontext";
import { useAuth } from "../auth/authcontext";

const { Title } = Typography;
const { Search } = Input;

type StatusFilter = "all" | "active" | "inactive";

const Languages: React.FC = () => {
    const [languages, setLanguages] = useState<LanguageDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editing, setEditing] = useState<LanguageDto | null>(null);
    const [form] = Form.useForm();

    const { userId, isReadOnly } = useAuth();
    const createdByGuid = userId || "";

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] =
        useState<StatusFilter>("all");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const showError = (msg: string) => {
        notification.error({
            message: "Error",
            description: msg,
            placement: "topRight"
        });
    };

    const fetchLanguages = async (
        page = 1,
        pageSize = pagination.pageSize,
        search = searchTerm,
        status = statusFilter
    ) => {
        setLoading(true);
        try {
            const offset = (page - 1) * pageSize;

            const res = await getLanguages(
                search,
                status,
                pageSize,
                offset
            );

            setLanguages(res.data.data);
            setPagination({
                current: page,
                pageSize,
                total: res.data.totalCount
            });
        } catch {
            showError("Failed to load languages");
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchLanguages(1, pagination.pageSize, "", "all");
    }, []);

    // Refetch when status changes
    useEffect(() => {
        fetchLanguages(1, pagination.pageSize, searchTerm, statusFilter);
    }, [statusFilter]);

    const handleSearch = (value: string) => {
        const trimmed = value.trim();
        setSearchTerm(trimmed);
        fetchLanguages(1, pagination.pageSize, trimmed, statusFilter);
    };

    const handleSearchChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!e.target.value) {
            setSearchTerm("");
            fetchLanguages(1, pagination.pageSize, "", statusFilter);
        }
    };

    const handleTableChange = (p: any) => {
        fetchLanguages(
            p.current,
            p.pageSize,
            searchTerm,
            statusFilter
        );
    };

    const openCreateDrawer = () => {
        setEditing(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const openEditDrawer = (record: LanguageDto) => {
        setEditing(record);
        form.setFieldsValue(record);
        setDrawerOpen(true);
    };

    const onSave = async () => {
        const values = await form.validateFields();

        try {
            if (editing) {
                await updateLanguage(editing.guid, values);
                message.success("Language updated");
            } else {
                await createLanguage({
                    ...values,
                    createdBy: createdByGuid
                });
                message.success("Language created");
            }

            setDrawerOpen(false);
            form.resetFields();
            fetchLanguages(
                pagination.current,
                pagination.pageSize,
                searchTerm,
                statusFilter
            );
        } catch {
            showError("Failed to save language");
        }
    };

    const toggleStatus = async (guid: string, active: boolean) => {
        try {
            await updateLanguageStatus(guid, active);
            message.success("Status updated");
            fetchLanguages(
                pagination.current,
                pagination.pageSize,
                searchTerm,
                statusFilter
            );
        } catch {
            showError("Failed to update status");
        }
    };

    const columns = [
        { title: "Name", dataIndex: "name" },
        { title: "Native Name", dataIndex: "nativeName" },
        { title: "ISO 639-1", dataIndex: "iso639_1" },
        {
            title: "Active",
            dataIndex: "active",
            render: (value: boolean, record: LanguageDto) => (
                <Popconfirm
                    title={value ? "Disable language" : "Enable language"}
                    description={
                        value
                            ? "Are you sure you want to disable this language?"
                            : "Are you sure you want to enable this language?"
                    }
                    okText="Yes"
                    cancelText="No"
                    onConfirm={() =>
                        toggleStatus(record.guid, !value)
                    }
                    disabled={isReadOnly}
                >
                    <Switch
                        checked={value}
                        size="small"
                        onClick={(_, e) => e.preventDefault()}
                        disabled={isReadOnly}
                    />
                </Popconfirm>
            )
        },
        {
            title: "Actions",
            render: (_: any, record: LanguageDto) => (
                <Tooltip title="Edit">
                    <Button
                        icon={
                            <EditOutlined
                                style={{ color: "var(--color-primary)" }}
                            />
                        }
                        onClick={() => openEditDrawer(record)}
                    />
                </Tooltip>
            )
        }
    ];

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>Languages</Title>

            <Space style={{ marginBottom: 16 }} wrap>
                <Search
                    placeholder="Search languages..."
                    allowClear
                    style={{ maxWidth: 400 }}
                    onSearch={handleSearch}
                    onChange={handleSearchChange}
                />

                <Segmented
                    value={statusFilter}
                    onChange={(val) =>
                        setStatusFilter(val as StatusFilter)
                    }
                    options={[
                        { label: "All", value: "all" },
                        { label: "Active", value: "active" },
                        { label: "Inactive", value: "inactive" }
                    ]}
                />

                <Button
                    color="primary"
                    variant="filled"
                    icon={<PlusOutlined />}
                    onClick={openCreateDrawer}
                >
                    Add Language
                </Button>
            </Space>

            {loading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table
                    rowKey="guid"
                    columns={columns}
                    dataSource={languages}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "50", "100"],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}–${range[1]} of ${total} languages`
                    }}
                    onChange={handleTableChange}
                />
            )}

            <LanguageDrawer
                open={drawerOpen}
                editing={editing}
                form={form}
                onClose={() => setDrawerOpen(false)}
                onSave={onSave}
            />
        </div>
    );
};

export default Languages;